import { EstatePlanningDocumentType } from '../Goals/goals/EstatePlanning/EstatePlanning.types';

export interface GoalDTO {
  GoalId: string;
  GoalName: string;
  GoalType: GoalTypeDTO;
  TabLabel?: string;
  RetirementAge?: number;
  LifeExpectancy?: number;
  GoalAssumptions: GoalAssumptionsDTO;
  GoalSummary: string;
  GoalExplanations: GoalExplanationListDTO[] | GoalExplanationsDTO;
  RetirementMonthlyExpenses?: number;
  GoalCharts?: GoalChartsListDTO[] | GoalChartsDTO;
  AllowCashflowCalibration?: boolean;
  FundDate?: number;
  LifeInsurance?: GoalLifeInsuranceDTO;
  DisabilityInsurance?: DisabilityInsuranceDTO;
  LongTermCareInsurance?: LongTermCareInsuranceDTO;
  RepaymentStrategy?: number;
  DebtOutputPerType?: DebtOutputPerTypeDTO[];
  DesiredPurchaseYear?: number;
  PulseCheckCategory: PulseCheckCategory;
  Documents: EstatePlanningDocumentDTO[];
}

export enum PulseCheckCategory {
  Happiness,
  Engagement,
  Advancement,
  Relationships,
  Truth,
  Strength,
}

export interface EstatePlanningDocumentDTO {
  Id: string;
  DocumentType: EstatePlanningDocumentType;
  Checked: boolean;
  Description: string | null;
  FileUrl: string | null;
  LastDocumentUpdate: string | null;
  Guardians?: string[];
  Notes: string | null;
}

export enum GoalTypeDTO {
  Retirement = 'retirement',
  EmergencyFund = 'emergencyfund',
  Education = 'education',
  Insurance = 'insurance',
  LargePurchase = 'largePurchase',
  DebtManagement = 'debtmanagement',
  HomePurchase = 'homePurchase',
  HealthCare = 'healthCare',
  EstatePlanning = 'estatePlanning',
  Decumulation = 'decumulation',
  CharitableGiving = 'charitableGiving',
}

export interface DebtOutputPerTypeDTO {
  TabLabel: string;
  DebtType: number;
  GoalExplanations: GoalExplanationsDTO;
  GoalCharts: GoalChartsDTO;
}

export interface GoalChartsDTO {
  Title: string;
  Data: GoalChartsDataDTO;
  Route: null;
  Type: string;
  IsEmpty: boolean;
  PdfOnly: boolean;
}

export interface GoalChartsDataDTO {
  Categories: number[];
  Series: GoalChartsDataSeriesDTO;
  YearsToPayOff: number;
  IsNeverPaidOff: boolean;
}

export interface GoalChartsDataSeriesDTO {
  Current: CurrentRecommendedDTO[];
  Recommended: CurrentRecommendedDTO[];
}

export interface CurrentRecommendedDTO {
  name: string;
  data: number[];
  stack: 'current' | 'recommended';
}

export interface GoalExplanationsDTO {
  Title: string;
  Message: string;
}

export interface DisabilityInsuranceDTO {
  Name: string;
  PrincipalName: string;
  SpouseName: string;
  BreakdownPrincipal: DisabilityInsuranceBreakdownDTO;
  BreakdownSpouse: DisabilityInsuranceBreakdownDTO;
  GoalCharts: DisabilityInsuranceGoalChartDTO[];
}

export interface DisabilityInsuranceBreakdownDTO {
  CoverageFactor: number;
  CurrentCoverage: number;
  MonthlySalary: number;
  PassiveIncome: number;
  CoverageLimit: number;
  CoverageNeed: number;
  CoverageGap: number;
}

export interface DisabilityInsuranceGoalChartDTO {
  Title: string;
  Data: DisabilityInsuranceGoalChartSeriesDTO;
  Route: null;
  Type: null;
  IsEmpty: boolean;
  PdfOnly: boolean;
}

export interface DisabilityInsuranceGoalChartSeriesDTO {
  IsEmpty: boolean;
  Series: Array<Array<number | string>>;
}

export interface GoalAssumptionsDTO {
  RetirementAge?: LifeExpectancyDTO[];
  RetirementLivingExpenses?: RetirementLivingExpensesDTO;
  LifeExpectancy?: LifeExpectancyDTO[];
  AdditionalSavingPerMonth?: number;
  PreRetirementAssetAllocation?: string;
  PreRetirementRateOfReturn?: number;
  PreRetirementStandardDeviation?: number;
  PostRetirementAssetAllocation?: string;
  PostRetirementRateOfReturn?: number;
  PostRetirementStandardDeviation?: number;
  SocialSecurityFilingAge?: LifeExpectancyDTO[];
  SocialSecurityAmount?: SocialSecurityAmountDTO[];
  InflationRate?: number;
  SocialSecurityInflationRate?: number;
  MarketDrop?: null;
  EarmarkedAssets?: number;
  TargetAmount?: number;
  TargetYear?: number;
  MonthsOfExpensesCovered?: number;
  SchoolName?: string;
  CostPerYear?: number;
  EnrollmentAge?: number;
  NumberOfYearsAttending?: number;
  AdditionalMonthlySavings?: number;
  AssetAllocation?: string;
  RateOfReturn?: number;
  LifeInsurance?: LifeInsuranceDTO;
  LongTermCare?: LongTermCareDTO;
  AdditionalSavingsPerMonth?: number;
  LoanPayoffStrategy?: null;
  PurchaseYear?: YearDTO;
  HomeValue?: number;
  DownPayment?: number;
  MortgageType?: null;
  MortgageInterest?: null;
  PropertyTaxAmount?: number;
  PMI?: number;
  HomeownersInsurance?: number;
  HOAFees?: number;
  AnnualCost?: AnnualCostDTO;
  GoalStartYear?: YearDTO;
  AdditionalSavings?: number;
  EstimateExpenses?: boolean;
}

export interface AnnualCostDTO {
  EstimateExpenses: boolean;
  AnnualCost: null;
}

export interface YearDTO {
  AtRetirement: boolean;
  Year: number | null;
}

export interface LifeExpectancyDTO {
  Name: string;
  Age: number;
}

export interface LifeInsuranceDTO {
  Married: MarriedDTO;
  SingleWithChildren: SingleWithChildrenDTO;
  SingleWithoutChildren: SingleWithoutChildrenDTO;
}

export interface MarriedDTO {
  PctOfLivingExpensesSpouseWillSpendWhenPrimaryDies: number;
  PctOfLivingExpensesPrimaryWillSpendWhenSpouseDies: number;
  PcOfCurrentIncomeSpouseWillMakeIfPrimaryDies: number;
  PctOfCurrentIncomePrimaryWillMakeIfSpouseDies: number;
  AdditionalLifeInsuranceToPurchaseOnPrimarysLife: number;
  AdditionalLifeInsuranceToPurchaseOnSpousesLife: number;
}

export interface SingleWithChildrenDTO {
  PctOfLivingExpensesEstateWouldSpend: number;
  AdditionalLifeInsuranceToPurchaseOnPrimarysLife: number;
}

export interface SingleWithoutChildrenDTO {
  AdditionalLifeInsuranceToPurchaseOnPrimarysLife: number;
}

export interface LongTermCareDTO {
  AgeWhenPrimaryBeginsCare: number;
  NumberOfYearsPrimaryWillBeInCare: number;
  AnnualCostOfCareForPrimary: number;
  ExistingDailyBenefitsForPrimary: number;
  AdditionalCarePurchasedForPrimary: number;
  AgeWhenSpouseBeginsCare: number;
  NumberOfYearsSpouseWillBeInCare: number;
  CostOfCareForSpouse: number;
  ExistingDailyBenefitsForSpouse: number;
}

export interface RetirementLivingExpensesDTO {
  amount: number;
  frequency: string;
}

export interface SocialSecurityAmountDTO {
  Name: string;
  Amount: number;
}

export interface GoalChartsListDTO {
  Title: string;
  Data: GoalChartsListDataListDTO[] | GoalChartsListDataDTO | null;
  Route: null | string;
  Type: null | string;
  IsEmpty: boolean;
  PdfOnly: boolean;
}

export interface GoalChartsListDataListDTO {
  Category: string;
  SocialSecurity: number;
  Pension: number;
  Annuity: number;
  TotalGuaranteedIncome: number;
  RetirementExpenses: number;
  GuaranteedIncomeShortage: number;
  TotalIncome: number;
  OtherIncome: number;
  Distributions: number;
  IncomeShortage: number;
}

export interface GoalChartsListDataDTO {
  PrincipalName?: string;
  PrincipalRetirementAge?: number;
  PrincipalRetirementIndex?: number;
  PrincipalLifeExpectancy?: number;
  PrincipalLifeExpectancyIndex?: number;
  HasSpouse?: boolean;
  SpouseName?: string;
  SpouseRetirementAge?: number;
  SpouseRetirementIndex?: number;
  SpouseLifeExpectancy?: number;
  SpouseLifeExpectancyIndex?: number;
  Categories?: Array<number | string>;
  Series: GoalChartsListDataSeriesDTO;
  IsEmpty?: boolean | string;
}

export interface GoalChartsListDataSeriesDTO {
  Recommended?: number[] | number;
  Current?: number[] | number;
  Expenses?: number[];
  Covered?: number[];
}

export interface GoalExplanationListDTO {
  Section: string;
  Title: string;
  Message: string;
  SubSection?: string;
  OwnerName?: string;
}

export interface GoalLifeInsuranceDTO {
  Name: string;
  PrincipalName: string;
  SpouseName: string;
  LifeExpectancyPrincipal: number;
  LifeExpectancySpouse: number;
  BreakdownPrincipal: LifeInsuranceBreakdownPrincipalDTO;
  BreakdownSpouse: LifeInsuranceBreakdownPrincipalDTO;
  GoalCharts: LifeInsuranceGoalChartDTO[];
}

export interface LifeInsuranceBreakdownPrincipalDTO {
  NeedsAnalysis: IncomeReplacementDTO[];
  NeedsAnalysisAssetsRunOut: string;
  NeedsAnalysisCurrentCoverage: number;
  NeedsAnalysisRecommendedCoverage: number;
  NeedsAnalysisCoverageGap: number;
  IncomeReplacement: IncomeReplacementDTO[];
  IncomeReplacementAssetsRunOut: string;
  IncomeReplacementCurrentCoverage: number;
  IncomeReplacementRecommendedCoverage: number;
  IncomeReplacementCoverageGap: number;
}

export interface IncomeReplacementDTO {
  Key: string;
  Value: number;
}

export interface LifeInsuranceGoalChartDTO {
  Title: string;
  Data: LifeInsuranceGoalChartDataDTO;
  Route: null;
  Type: string;
  IsEmpty: boolean;
  PdfOnly: boolean;
}

export interface LifeInsuranceGoalChartDataDTO {
  IsEmpty: boolean;
  Series: LifeInsuranceGoalChartDataSeriesDTO;
}

export interface LifeInsuranceGoalChartDataSeriesDTO {
  Recommended: number;
  Current: number;
}

export interface LongTermCareInsuranceDTO {
  Name: string;
  PrincipalName: string;
  SpouseName: string;
  PrincipalStartAge: number;
  SpouseStartAge: number;
  PrincipalDuration: number;
  SpouseDuration: number;
  BreakdownPrincipal: LongTermCareInsuranceBreakdownDTO;
  BreakdownSpouse: LongTermCareInsuranceBreakdownDTO;
  GoalCharts: LongTermCareInsuranceGoalChartDTO[];
}

export interface LongTermCareInsuranceBreakdownDTO {
  AssetsToLiquidate: number;
  AnnualLTCCost: number;
  DailyBenefit: number;
  AnnualInsuranceCost: number;
}

export interface LongTermCareInsuranceGoalChartDTO {
  Title: string;
  Data: LongTermCareInsuranceGoalChartDataDTO;
  Route: null;
  Type: null;
  IsEmpty: boolean;
  PdfOnly: boolean;
}

export interface LongTermCareInsuranceGoalChartDataDTO {
  Categories: string[];
  Series: LongTermCareInsuranceGoalChartDataSeriesDTO;
}

export interface LongTermCareInsuranceGoalChartDataSeriesDTO {
  Recommended: number[];
  Current: number[];
}

export interface GoalChartDTO<T> {
  Data: T;
  Title: string;
  Route: null | string;
  Type: null | string;
  IsEmpty: boolean;
  PdfOnly: boolean;
}
